import React, { createContext, useContext, useState, useEffect, FC, useMemo, useRef } from 'react';
import axios from 'axios';
import { useQuery, useQueryClient } from 'react-query';
import * as signalR from '@microsoft/signalr';
import { getDashboard, getDataById } from './campaign-list/core/_requests';

interface AgentContextType {
  isLoggedIn: boolean;
  setIsLoggedIn: (v: boolean) => void;
  isAux: boolean;
  setIsAux: (v: boolean) => void;
  selectedCampaign: string;
  setSelectedCampaign: (v: string) => void;
  campaigns: any[];
  auxReasons: any[];
  seconds: number;
  handleLogin: () => void;
  handleLogout: () => void;
  handleAuxAction: (id: number | null) => void;
  agentExt: string;
  token: string;
  data: any;
  campaignMetrics: any;
  activeCampaignData: any;
  fullCampaignDetail: any;
  auxReasonName: string;
  isCampaignDetailLoading: boolean;
}

const AgentContext = createContext<AgentContextType | undefined>(undefined);

export const useAgentContext = () => {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error('useAgentContext must be used within an AgentProvider');
  }
  return context;
};

export const AgentProvider: FC<{children: React.ReactNode}> = ({ children }) => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
  
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isAux, setIsAux] = useState<boolean>(false);
  const [selectedCampaign, setSelectedCampaign] = useState('Select Campaign');
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [auxReasons, setAuxReasons] = useState<any[]>([]);
  const [seconds, setSeconds] = useState(0);

  const [data, setData] = useState<any>({
    stats: { inbound: 0, outbound: 0, serviceLevel: '0%', avgTalkTime: '00:00' },
    queue: [],
    performance: { current: 0, target: 100 },
    abandoned: []
  });

  const esRef = useRef<EventSource | null>(null);
  const queryClient = useQueryClient();

  const getApiToken = () => {
    try {
      const raw = localStorage.getItem('kt-auth-react-v');
      if (!raw) return { token: '', ext: '' };
      const parsed = JSON.parse(raw);
      return {
        token: parsed.api_token || '',
        ext: parsed.extension || ''
      };
    } catch { return { token: '', ext: '' }; }
  };

  const { token, ext: agentExt } = getApiToken();
  const config = { headers: { Authorization: `Bearer ${token}` } };

  // Verify Agent
  useQuery(
    ['verify-agent-context', agentExt],
    async () => {
      const res = await axios.get(`${apiUrl}/dashboard/verify`, config);
      return res.data;
    },
    {
      enabled: !!token && !!agentExt,
      refetchOnWindowFocus: false,
      staleTime: 0,
      onSuccess: (resData) => {
        if (resData.authenticated && resData.isLoggedIn) {
          setIsLoggedIn(true);
          setIsAux(resData.isAux);
          setSelectedCampaign(resData.campaignName || 'Select Campaign');
          if (resData.loginAt) {
            localStorage.setItem('agent_login_at', resData.loginAt);
          }
        } else {
          setIsLoggedIn(false);
          setIsAux(false);
        }
      },
      onError: () => {
        setIsLoggedIn(false);
        setIsAux(false);
      }
    }
  );

  // Fetch Metadata
  useEffect(() => {
    const fetchMetadata = async () => {
      if (!token) return;
      try {
        const [resAux, resCamp] = await Promise.all([
          axios.get(`${apiUrl}/dashboard/aux-reasons`, config),
          axios.get(`${apiUrl}/dashboard/campaigns`, config)
        ]);
        setAuxReasons(resAux.data);
        const inboundOption = { id: 0, name: "inbound" };
        setCampaigns([inboundOption, ...resCamp.data]);
      } catch (err) {
        console.error("Failed to fetch metadata:", err);
      }
    };
    fetchMetadata();
  }, [token]);

  // Timer logic
  useEffect(() => {
    if (isLoggedIn) {
      const syncTime = () => {
        const loginAt = localStorage.getItem('agent_login_at');
        if (loginAt) {
          const startTime = new Date(loginAt).getTime();
          const now = new Date().getTime();
          const diffInSeconds = Math.floor((now - startTime) / 1000);
          setSeconds(diffInSeconds > 0 ? diffInSeconds : 0);
        }
      };
      syncTime();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    let interval: any;
    if (isLoggedIn && !isAux) interval = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(interval);
  }, [isLoggedIn, isAux]);

  const handleLogin = async () => {
    if (selectedCampaign === 'Select Campaign') return alert("Pilih Campaign dulu!");
    try {
      const res = await axios.post(`${apiUrl}/dashboard/login`, { campaignName: selectedCampaign }, config);
      if (res.data.success) {
        setIsLoggedIn(true);
        // localStorage.setItem('agent_login_at', new Date().toLocaleString());  
      }
    } catch (err) { alert("Login Gagal!"); }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${apiUrl}/dashboard/logout`, {}, config);
    } finally {
      setIsLoggedIn(false);
      setIsAux(false);
      localStorage.removeItem('agent_login_at');
      window.location.reload();
    }
  };

  const [auxReasonName, setAuxReasonName] = useState<string>('');

  const handleAuxAction = async (id: number | null) => {
    const isStarting = id !== null;
    const url = isStarting ? `${apiUrl}/dashboard/aux/start` : `${apiUrl}/dashboard/aux/stop`;
    try {
      await axios.post(url, isStarting ? { auxReasonId: id } : {}, config);
      setIsAux(isStarting);
      if (isStarting) {
        const reason = auxReasons.find((r: any) => r.id === id);
        setAuxReasonName(reason ? reason.reasonName : 'Break');
      } else {
        setAuxReasonName('');
      }
      localStorage.setItem('agent_is_aux', isStarting.toString());
    } catch (err) { console.error("AUX Error", err); }
  };

  // --- METRICS & REALTIME DATA ---
  
  const activeCampaignData = useMemo(() => {
    return campaigns.find(c => (c.name === selectedCampaign || c.Name === selectedCampaign) && c.name !== 'inbound' && c.Name !== 'inbound');
  }, [selectedCampaign, campaigns]);

  const { data: fullCampaignDetail, isLoading: isCampaignDetailLoading } = useQuery(
    ['campaign-detail', activeCampaignData?.id || activeCampaignData?.Id || activeCampaignData?.ID],
    async () => {
      const cid = activeCampaignData?.id || activeCampaignData?.Id || activeCampaignData?.ID;
      if (!cid) return null;
      const response = await getDataById(cid as string);
      return (response as any).data || response;
    },
    {
      enabled: !!(activeCampaignData?.id || activeCampaignData?.Id || activeCampaignData?.ID),
      refetchOnWindowFocus: false,
      staleTime: 300000,
    }
  );

  const { data: campaignMetrics } = useQuery(
    ['campaign-metrics', activeCampaignData?.id || activeCampaignData?.Id || activeCampaignData?.ID],
    async () => {
      const cid = activeCampaignData?.id || activeCampaignData?.Id || activeCampaignData?.ID;
      if (!cid) return null;
      const response = await getDashboard(cid as string);
      return response?.stats || response;
    },
    {
      enabled: !!(activeCampaignData?.id || activeCampaignData?.Id || activeCampaignData?.ID) && activeCampaignData?.name !== 'inbound' && activeCampaignData?.Name !== 'inbound',
      refetchOnWindowFocus: false,
    }
  );

  // SSE for general agent stats
  useEffect(() => {
    if (isLoggedIn) {
      const sseUrl = `${apiUrl}/dashboard/agent-stats${apiUrl.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
      const es = new EventSource(sseUrl);
      esRef.current = es;
      es.onmessage = (e) => setData(JSON.parse(e.data));
      
      return () => es.close();
    }
  }, [isLoggedIn, apiUrl, token]);

  // SignalR for campaign metrics
  useEffect(() => {
    const cid = activeCampaignData?.id || activeCampaignData?.Id;
    if (!cid || activeCampaignData?.name === 'inbound' || activeCampaignData?.Name === 'inbound') return;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${apiUrl}/statushub`, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
        accessTokenFactory: () => {
          const raw = localStorage.getItem('kt-auth-react-v');
          if (!raw) return '';
          try { return JSON.parse(raw).api_token || ''; } catch { return ''; }
        }
      })
      .withAutomaticReconnect()
      .build();

    connection.on('callStatusUpdated', (payload: any) => {
      if (String(payload.campaignId).toLowerCase() !== String(cid).toLowerCase()) return;

      queryClient.setQueryData(['campaign-metrics', cid], (prev: any) => {
        if (!prev) return prev;
        const isNewCall = payload.status === 'calling' && payload.disposition === 'CALLING';
        const isAnswered = payload.disposition === 'ANSWERED';
        const isFailed = payload.disposition === 'NO ANSWER' || payload.disposition === 'BUSY' || payload.disposition === 'FAILED';

        let newStats = { ...prev };
        if (isNewCall) newStats.totalCalls = (newStats.totalCalls || 0) + 1;
        if (isAnswered) {
          newStats.connected = (newStats.connected || 0) + 1;
          newStats.success = (newStats.success || 0) + 1;
        } else if (isFailed) {
          newStats.notConnected = (newStats.notConnected || 0) + 1;
          newStats.failed = (newStats.failed || 0) + 1;
        }
        return newStats;
      });
    });

    let startPromise = connection.start().catch(e => console.warn('[SignalR] Agent Dashboard hub error:', e));
    return () => { 
      startPromise.then(() => connection.stop()); 
    };
  }, [activeCampaignData?.id, queryClient, apiUrl]);

  return (
    <AgentContext.Provider
      value={{
        isLoggedIn, setIsLoggedIn,
        isAux, setIsAux,
        selectedCampaign, setSelectedCampaign,
        campaigns, auxReasons,
        seconds,
        handleLogin, handleLogout, handleAuxAction,
        agentExt, token,
        data, campaignMetrics, activeCampaignData, fullCampaignDetail, auxReasonName,
        isCampaignDetailLoading
      }}
    >
      {children}
    </AgentContext.Provider>
  );
};
